import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityDependencyOfComponentsCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import { CustomAboutCard } from './CustomAboutCard';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { DocsViewerContent, isDocsViewerAvailable } from '../docs-viewer';
import { MobileBuildsContent, isMobileApp } from '../mobile-builds';
import { ApiChangesCard } from '../api-changes';
import { InitiativeDeployCard, InitiativeVersionsCard, isInitiative } from '../initiative-deploy';
import { SnykIgnoresCard } from '../snyk-ignores';
import { EnvVarsRuntimeContent, isEnvVarsRuntimeAvailable } from '../envvars-runtime';
import { DynatraceMetricsCard } from '../dynatrace-metrics';
import { DeployedVersionsCard, DeploymentHistoryCard, VersionsCard } from '../deployed-versions';
import { RollbackLink } from '../releases';
import { ApiDownloadCard } from '../api-download/ApiDownloadCard';
import { ServiceHealthCard } from '../health';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import { KubernetesPodLogs } from '../kubernetes';

import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';

import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import { isSonarQubeAvailable } from '@backstage-community/plugin-sonarqube-react';
import { SonarQubeRelatedEntitiesOverview } from '@backstage-community/plugin-sonarqube';

import { EntityJiraDashboardContent, isJiraDashboardAvailable } from '@axis-backstage/plugin-jira-dashboard';
import { EntityTeamPullRequestsContent } from '@backstage-community/plugin-github-pull-requests-board';
import { DynatraceTab, isDynatraceAvailable } from '@backstage-community/plugin-dynatrace';
import { EntitySnykContent as SnykPluginContent, isSnykAvailable } from 'backstage-plugin-snyk';
import {
  EntityGrafanaAlertsCard,
  EntityGrafanaDashboardsCard,
  isDashboardSelectorAvailable,
  isAlertSelectorAvailable,
} from '@backstage-community/plugin-grafana';

const MISSING_ANNOTATION_READ_MORE_URL = 'https://backstage.io/docs/features/software-catalog/descriptor';

// Pestaña Snyk: las excepciones del .snyk del repo encima de lo que muestra el plugin.
const EntitySnykContent = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <SnykIgnoresCard />
    </Grid>
    <Grid item xs={12}>
      <SnykPluginContent />
    </Grid>
  </Grid>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>

  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      {/* <EntityAboutCard variant="gridItem" /> */}
      <CustomAboutCard variant="gridItem" {...{ subheader: <></> }} />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>

    <EntitySwitch>
      <EntitySwitch.Case if={isKubernetesAvailable}>
        <Grid item md={6} xs={12}>
          <DeployedVersionsCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    {/* Sin pestaña Snyk, las excepciones del .snyk van en el resumen (solo si hay) */}
    <EntitySwitch>
      <EntitySwitch.Case if={entity => !isSnykAvailable(entity)}>
        <Grid item xs={12}>
          <SnykIgnoresCard hideEmpty />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={isDynatraceAvailable}>
        <Grid item md={6} xs={12}>
          <DynatraceMetricsCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={entity => isComponentType('microservice')(entity) && isGithubActionsAvailable(entity)}>
        <Grid item xs={12}>
          <DeploymentHistoryCard />
        </Grid>
        {/* Solo devops y arquitectos: para el resto no se pinta */}
        <Grid item xs={12}>
          <RollbackLink />
        </Grid>
        <Grid item md={6} xs={12}>
          <ServiceHealthCard />
        </Grid>
        <Grid item xs={12}>
          <VersionsCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <Grid item md={6} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
    <EntitySwitch>
      <EntitySwitch.Case if={isSonarQubeAvailable}>
        <Grid item md={6}>
      <EntitySonarQubeCard variant="gridItem" missingAnnotationReadMoreUrl={MISSING_ANNOTATION_READ_MORE_URL} />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>


  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isKind('component')} path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/pull-requests" title="Pull Requests" if={isDocsViewerAvailable}>
      <EntityTeamPullRequestsContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityKubernetesContent refreshIntervalMs={30000} />
        </Grid>
        <Grid item xs={12}>
          <KubernetesPodLogs />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/envvars"
      title="Env Vars"
      if={isEnvVarsRuntimeAvailable}
    >
      <EnvVarsRuntimeContent />
    </EntityLayout.Route>

    <EntityLayout.Route if={isKind('component')} path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependencyOfComponentsCard variant="gridItem" title="Lo consumen" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isDocsViewerAvailable}>
      <DocsViewerContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/jira-dashboard" title="Jira Dashboard" if={entity => isJiraDashboardAvailable(entity, 'jira')}>
      <EntityJiraDashboardContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dynatrace" title="Dynatrace" if={isDynatraceAvailable}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DynatraceMetricsCard />
        </Grid>
        <Grid item xs={12}>
          <DynatraceTab />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/snyk" title="Snyk" if={isSnykAvailable}>
      <EntitySnykContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/grafana"
      title="Grafana"
      if={entity => isDashboardSelectorAvailable(entity) !== undefined || isAlertSelectorAvailable(entity)}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGrafanaDashboardsCard />
        </Grid>
        <Grid item xs={12}>
          <EntityGrafanaAlertsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isKind('component')} path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/pull-requests" title="Pull Requests" if={isDocsViewerAvailable}>
      <EntityTeamPullRequestsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependencyOfComponentsCard variant="gridItem" title="Lo consumen" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isDocsViewerAvailable}>
      <DocsViewerContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dynatrace" title="Dynatrace" if={isDynatraceAvailable}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DynatraceMetricsCard />
        </Grid>
        <Grid item xs={12}>
          <DynatraceTab />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/snyk" title="Snyk" if={isSnykAvailable}>
      <EntitySnykContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/grafana"
      title="Grafana"
      if={entity => isDashboardSelectorAvailable(entity) !== undefined || isAlertSelectorAvailable(entity)}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGrafanaDashboardsCard />
        </Grid>
        <Grid item xs={12}>
          <EntityGrafanaAlertsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/builds" title="Builds" if={isMobileApp}>
      <MobileBuildsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/pull-requests" title="Pull Requests" if={isDocsViewerAvailable}>
      <EntityTeamPullRequestsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isDocsViewerAvailable}>
      <DocsViewerContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={entity => isJiraDashboardAvailable(entity, 'jira')}
      path="/jira-dashboard"
      title="Jira Dashboard"
    >
      <EntityJiraDashboardContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dynatrace" title="Dynatrace" if={isDynatraceAvailable}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DynatraceMetricsCard />
        </Grid>
        <Grid item xs={12}>
          <DynatraceTab />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/snyk" title="Snyk" if={isSnykAvailable}>
      <EntitySnykContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/grafana"
      title="Grafana"
      if={entity => isDashboardSelectorAvailable(entity) !== undefined || isAlertSelectorAvailable(entity)}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityGrafanaDashboardsCard />
        </Grid>
        <Grid item xs={12}>
          <EntityGrafanaAlertsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('microservice')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          {/* <EntityAboutCard /> */}
          <CustomAboutCard {...{ subheader: <></> } as any} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ApiChangesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ApiDownloadCard />
        </Grid>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          {/* <EntityAboutCard variant="gridItem" /> */}
          <CustomAboutCard variant="gridItem" {...{ subheader: <></> }} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isInitiative}>
            <Grid item xs={12}>
              <InitiativeVersionsCard />
            </Grid>
            <Grid item xs={12}>
              <InitiativeDeployCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
    <EntityLayout.Route path="/sonarqube" title="Code Quality">
      <SonarQubeRelatedEntitiesOverview relationType={RELATION_HAS_PART} entityKind="component" />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          {/* <EntityAboutCard variant="gridItem" /> */}
          <CustomAboutCard variant="gridItem" {...{ subheader: <></> }} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const resourcePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <CustomAboutCard variant="gridItem" {...{ subheader: <></> }} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={isDocsViewerAvailable}>
      <DocsViewerContent />
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('Component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('API')} children={apiPage} />
    <EntitySwitch.Case if={isKind('Group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('User')} children={userPage} />
    <EntitySwitch.Case if={isKind('System')} children={systemPage} />
    <EntitySwitch.Case if={isKind('Domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('Resource')} children={resourcePage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
